package co.maytes.standupbot.dashboard.features.settings.sections

import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.features.settings.phase
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.features.integrations.AnthropicPanel
import co.maytes.standupbot.dashboard.features.integrations.AppCredentialsStore
import co.maytes.standupbot.dashboard.features.integrations.LoadState
import co.maytes.standupbot.dashboard.features.integrations.rememberAppCredentialsStore
import co.maytes.standupbot.dashboard.features.onboarding.IntegrationsLoadState
import co.maytes.standupbot.dashboard.features.onboarding.IntegrationsStore
import co.maytes.standupbot.dashboard.features.settings.GithubWatchedReposPanel
import co.maytes.standupbot.dashboard.features.settings.IntegrationStatusPanel
import co.maytes.standupbot.dashboard.features.settings.IntegrationsCopy
import co.maytes.standupbot.dashboard.features.settings.Plain
import co.maytes.standupbot.dashboard.features.settings.ServiceSnapshot
import co.maytes.standupbot.dashboard.features.settings.SettingsBars
import co.maytes.standupbot.dashboard.features.settings.SettingsError
import co.maytes.standupbot.dashboard.features.settings.SettingsGroup
import co.maytes.standupbot.dashboard.features.settings.SettingsPage
import co.maytes.standupbot.dashboard.features.settings.SettingsPanel
import co.maytes.standupbot.dashboard.features.settings.SettingsRowItem
import co.maytes.standupbot.dashboard.features.settings.SettingsSectionRow
import co.maytes.standupbot.dashboard.features.settings.SettingsSubtitle
import co.maytes.standupbot.dashboard.features.settings.Strong
import co.maytes.standupbot.dashboard.features.setup.SetupCheckReportView
import co.maytes.standupbot.dashboard.features.setup.TestMySetupButton
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberSetupCheckStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.integration.IntegrationProviderDto
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import org.w3c.dom.url.URLSearchParams

@Composable
fun IntegrationsSection() {
    val auth = rememberAuthStore()
    val api = rememberApiClient()
    val scope = rememberCoroutineScope()
    val credsStore = rememberAppCredentialsStore()
    val integrationsStore = remember(api) { IntegrationsStore(api) }
    val workspaceId = auth.currentUser?.workspace?.id

    LaunchedEffect(workspaceId) {
        val ws = workspaceId ?: return@LaunchedEffect
        if (ws.isNotBlank()) {
            credsStore.refresh(ws)
            integrationsStore.refresh(ws)
        }
    }

    val checkState = rememberSetupCheckStore().state
    val checkedReport = checkState.report
    LaunchedEffect(checkedReport) {
        val ws = workspaceId
        if (checkedReport != null && !ws.isNullOrBlank()) integrationsStore.refresh(ws)
    }

    val fixTarget = remember { URLSearchParams(window.location.search).get("fix") }
    val everythingLoaded = integrationsStore.loadState == IntegrationsLoadState.Ready &&
        credsStore.listState == LoadState.Ready
    LaunchedEffect(fixTarget, everythingLoaded) {
        if (fixTarget != null && everythingLoaded) {
            document.getElementById("integration-$fixTarget")?.scrollIntoView()
        }
    }

    val githubSuspended = auth.currentUser?.workspace?.githubSuspended == true
    val ready = integrationsStore.loadState == IntegrationsLoadState.Ready

    SettingsPage(
        group = SettingsGroup.Workspace,
        title = "Integrations",
        subtitle = if (ready) {
            { ServicesSubtitle(integrationsStore, githubSuspended) }
        } else {
            null
        },
        action = { TestMySetupButton(variant = ButtonVariant.Secondary, size = ButtonSize.Md) },
    ) {
        SpanText(
            text = IntegrationsCopy.CHECK_HINT,
            modifier = Modifier
                .margin(bottom = Spacing.md)
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textSecondary),
        )
        if (checkedReport != null || checkState.running || checkState.error != null) {
            Box(modifier = Modifier.margin(bottom = Spacing.md)) {
                SettingsPanel { SetupCheckReportView() }
            }
        }

        ServicesSection(
            workspaceId = workspaceId,
            store = integrationsStore,
            facts = IntegrationsCopy.checkFacts(checkedReport, Clock.System.now()),
            onRetry = { scope.launch { workspaceId?.let { integrationsStore.refresh(it) } } },
        )
        if (ready && integrationsStore.isConnected(IntegrationProviderDto.GITHUB)) {
            SettingsSectionRow(
                anchor = "github-repos",
                title = "Watched GitHub repositories",
                description = "Pull requests from these repositories feed the standup. Leave it empty to scan every " +
                    "repository the install can see.",
            ) {
                SettingsPanel {
                    GithubWatchedReposPanel(workspaceId = workspaceId, store = integrationsStore)
                }
            }
        }
        AiSection(
            workspaceId = workspaceId,
            credsStore = credsStore,
            integrationsStore = integrationsStore,
            onRetry = { scope.launch { workspaceId?.let { credsStore.refresh(it) } } },
        )
    }
}

@Composable
private fun ServicesSubtitle(store: IntegrationsStore, githubSuspended: Boolean) {
    val snapshots = IntegrationsCopy.services.map { provider ->
        val suspended = provider == IntegrationProviderDto.GITHUB && githubSuspended
        ServiceSnapshot(
            name = IntegrationsCopy.displayName(provider),
            level = IntegrationsCopy.level(store.statusOf(provider), suspended),
        )
    }
    val parts = IntegrationsCopy.subtitle(snapshots)
    SettingsSubtitle {
        Strong(parts.strong)
        Plain(parts.plain)
    }
}

@Composable
private fun ServicesSection(
    workspaceId: String?,
    store: IntegrationsStore,
    facts: List<Pair<String, String>>,
    onRetry: () -> Unit,
) {
    SettingsSectionRow(
        anchor = "services",
        title = "Services",
        description = "Slack and Linear connect through the StandupBot app, so there are no secrets to paste.",
        aside = facts,
    ) {
        SettingsPanel {
            when (store.loadState) {
                IntegrationsLoadState.Idle, IntegrationsLoadState.Loading -> SettingsBars(rows = IntegrationsCopy.services.size)
                is IntegrationsLoadState.Error -> SettingsError(what = "the connection status", onRetry = onRetry)
                IntegrationsLoadState.Ready -> {
                    IntegrationsCopy.services.forEachIndexed { index, provider ->
                        IntegrationStatusPanel(
                            workspaceId = workspaceId,
                            provider = provider,
                            store = store,
                            first = index == 0,
                        )
                    }
                    SyncMembersRow(workspaceId = workspaceId, store = store)
                }
            }
        }
    }
}

@Composable
private fun SyncMembersRow(workspaceId: String?, store: IntegrationsStore) {
    val ui = rememberUiStore()
    val scope = rememberCoroutineScope()
    var syncing by remember { mutableStateOf(false) }

    SettingsRowItem(
        title = "Link people to their accounts",
        description = "Matches your members to their Linear and GitHub accounts.",
        trailing = {
            Button(
                label = if (syncing) "Syncing…" else "Re-sync members",
                variant = ButtonVariant.Secondary,
                size = ButtonSize.Sm,
                enabled = !syncing && !workspaceId.isNullOrBlank(),
                loading = syncing,
                onClick = {
                    val ws = workspaceId ?: return@Button
                    scope.launch {
                        syncing = true
                        when (val r = store.syncMembers(ws)) {
                            is Result.Ok -> ui.showToast(IntegrationsCopy.syncSummary(r.value), ToastKind.Success)
                            is Result.Err -> ui.showToast("Sync failed: ${r.error.displayMessage}", ToastKind.Error)
                        }
                        syncing = false
                    }
                },
            )
        },
    )
}

@Composable
private fun AiSection(
    workspaceId: String?,
    credsStore: AppCredentialsStore,
    integrationsStore: IntegrationsStore,
    onRetry: () -> Unit,
) {
    SettingsSectionRow(
        anchor = "ai",
        title = "AI",
        description = "Bring your own Anthropic key so synthesis cost is attributed to your workspace.",
    ) {
        SettingsPanel {
            StateSwap(state = credsStore.listState.phase()) {
                when (credsStore.listState) {
                    LoadState.Idle, LoadState.Loading -> SettingsBars(rows = 2)
                    is LoadState.Error -> SettingsError(what = "the Anthropic key", onRetry = onRetry)
                    LoadState.Ready -> AnthropicPanel(
                        workspaceId = workspaceId,
                        store = credsStore,
                        health = integrationsStore.statusOf(IntegrationProviderDto.ANTHROPIC),
                    )
                }
            }
        }
    }
}
